// HQ → Agent settings: what the WhatsApp agent and the voice agent do, for the organization default or one
// salon chosen on this page. A salon only stores what HQ changed for it; everything else follows the
// organization. Cross-branch rules (booking gap, marketing limit) are organization-wide.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { SettingsBranchSelect, useEngagementScope } from '../context/EngagementScope';
import { Loader, PageHead, Spinner } from '../components/ui';

const WHATSAPP_FIELDS = [
  { key: 'greeting', label: 'Greeting', hint: 'How the agent greets a new customer. Leave empty for the default.', multiline: true },
  { key: 'tone', label: 'Tone', hint: 'For example: warm and friendly, or polite and formal.' },
  { key: 'handoff_rules', label: 'Also hand the chat to the team when…', hint: 'Extra cases where staff should take over.', multiline: true },
  { key: 'extra_instructions', label: 'Extra instructions', hint: 'Anything else the agent should follow (offers, policies to mention, things to avoid).', multiline: true },
];
const VOICE_FIELDS = [{ key: 'language', label: 'Preferred language', hint: 'For example: Tamil, English, Hindi. Empty = the caller’s language.' }];

function Switch({ on, onChange, disabled }) {
  return (
    <button type="button" className={`de-switch${on ? ' on' : ''}`} disabled={disabled} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="de-switch-knob" />
    </button>
  );
}

function ProfileForm({ channel, fields, franchiseId }) {
  const { branchName } = useEngagementScope();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const branchView = Boolean(franchiseId);

  const load = () =>
    api
      .getAgentProfile(channel, franchiseId)
      .then((d) => {
        setData(d);
        setDraft({});
      })
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, franchiseId]);

  if (!data) return <Loader />;
  const canEdit = data.can_edit;
  const value = (key) => (key in draft ? draft[key] : data.effective[key]);
  const overridden = (key) => branchView && key in (data.overrides || {});
  const changed = Object.keys(draft).length > 0;

  const save = async (changes = draft) => {
    setSaving(true);
    try {
      const d = await api.saveAgentProfile(channel, changes, franchiseId);
      setData(d);
      setDraft({});
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="de-card">
      <div className="de-row de-row--between de-row--wrap" style={{ gap: 12, marginBottom: 14 }}>
        <div>
          <div className="de-strong">{channel === 'voice' ? 'Voice agent' : 'WhatsApp agent'}</div>
          <div className="de-small de-muted">
            {branchView
              ? `${branchName(franchiseId)}: fields marked “Branch” are this branch’s own; the rest follow the organization.`
              : 'Organization default: every branch uses these unless HQ changes a branch.'}
          </div>
        </div>
        <div className="de-row" style={{ gap: 10, alignItems: 'center' }}>
          <span className="de-small">{value('enabled') ? 'On' : 'Off'}</span>
          <Switch on={Boolean(value('enabled'))} disabled={!canEdit} onChange={(on) => setDraft((d) => ({ ...d, enabled: on }))} />
          {overridden('enabled') ? <span className="de-pill de-pill--gold de-pill--sm">Branch</span> : null}
        </div>
      </div>
      {channel === 'voice' ? (
        <div className="de-small de-muted" style={{ marginBottom: 12 }}>
          Turned off for a branch, the voice agent takes the caller’s details and asks a teammate to call back instead of booking.
          The voice agent’s own prompt and tools are set in the Sarvam dashboard.
        </div>
      ) : (
        <div className="de-small de-muted" style={{ marginBottom: 12 }}>
          Turned off for a branch, the AI stays silent in that branch’s chats and a human-intervention request is raised so staff reply.
          Services and prices always come from the branch’s service list.
        </div>
      )}
      <div className="de-form-grid">
        {fields.map((f) => (
          <label key={f.key} className="de-field">
            <span className="de-label">
              {f.label} {overridden(f.key) ? <span className="de-pill de-pill--gold de-pill--sm">Branch</span> : null}
            </span>
            {f.multiline ? (
              <textarea
                className="de-textarea"
                rows={3}
                value={value(f.key) || ''}
                disabled={!canEdit}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
              />
            ) : (
              <input className="de-input de-input--bordered" value={value(f.key) || ''} disabled={!canEdit} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
            )}
            <span className="de-small de-muted">
              {f.hint}
              {branchView && !overridden(f.key) && data.organization[f.key] ? ` Organization: “${data.organization[f.key]}”.` : ''}
            </span>
            {overridden(f.key) && canEdit ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => save({ [f.key]: null })}>
                Use the organization’s value
              </button>
            ) : null}
          </label>
        ))}
      </div>
      {canEdit ? (
        <div className="de-form-actions" style={{ marginTop: 16 }}>
          {branchView && overridden('enabled') ? (
            <button type="button" className="de-btn de-btn--outline" onClick={() => save({ enabled: null })} disabled={saving}>
              On/off: follow the organization
            </button>
          ) : null}
          <button type="button" className="de-btn de-btn--outline" disabled={!changed || saving} onClick={() => setDraft({})}>
            Discard
          </button>
          <button type="button" className="de-btn de-btn--primary" disabled={!changed || saving} onClick={() => save()}>
            {saving ? <Spinner light /> : null}
            Save
          </button>
        </div>
      ) : (
        <div className="de-small de-muted" style={{ marginTop: 12 }}>
          Only HQ can change these.
        </div>
      )}
    </div>
  );
}

function BranchRules() {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getBranchRules().then(setRules).catch((e) => toast.error(e.message));
  }, []);

  if (!rules) return <Loader />;
  const value = (k) => (k in draft ? draft[k] : rules[k]);
  const save = async () => {
    setSaving(true);
    try {
      const out = await api.saveBranchRules(Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, Number(v)])));
      setRules(out);
      setDraft({});
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="de-card">
      <div className="de-strong">Customers who use several branches</div>
      <div className="de-small de-muted" style={{ marginBottom: 14 }}>Organization-wide; applies to every branch.</div>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">Minimum gap between a customer’s bookings at different branches</span>
          <input
            className="de-input de-input--bordered"
            type="number"
            min={rules.limits?.booking_gap_minutes?.[0] ?? 0}
            max={rules.limits?.booking_gap_minutes?.[1] ?? 1440}
            value={value('booking_gap_minutes')}
            disabled={!rules.can_edit}
            onChange={(e) => setDraft((d) => ({ ...d, booking_gap_minutes: e.target.value }))}
          />
          <span className="de-small de-muted">Minutes. The agent offers another time; the desk sees a warning and can book anyway.</span>
        </label>
        <label className="de-field">
          <span className="de-label">At most one marketing message per customer every</span>
          <input
            className="de-input de-input--bordered"
            type="number"
            min={rules.limits?.nudge_limit_hours?.[0] ?? 0}
            max={rules.limits?.nudge_limit_hours?.[1] ?? 168}
            value={value('nudge_limit_hours')}
            disabled={!rules.can_edit}
            onChange={(e) => setDraft((d) => ({ ...d, nudge_limit_hours: e.target.value }))}
          />
          <span className="de-small de-muted">Hours, across every branch and number. Booking confirmations and reminders are never held back. 0 = no limit.</span>
        </label>
      </div>
      {rules.can_edit ? (
        <div className="de-form-actions" style={{ marginTop: 16 }}>
          <button type="button" className="de-btn de-btn--primary" disabled={!Object.keys(draft).length || saving} onClick={save}>
            {saving ? <Spinner light /> : null}
            Save
          </button>
        </div>
      ) : null}
    </div>
  );
}

const TABS = [
  { key: 'whatsapp', label: 'WhatsApp agent' },
  { key: 'voice', label: 'Voice agent' },
  { key: 'rules', label: 'Cross-branch rules' },
];

export default function AgentSettings() {
  const { selectedIds, branchName } = useEngagementScope();
  const [tab, setTab] = useState('whatsapp');
  const [salon, setSalon] = useState(() => (selectedIds.length === 1 ? selectedIds[0] : ''));
  return (
    <div className="de-fade">
      <PageHead
        title="Agent settings"
        sub={salon ? `${branchName(salon)}: this branch’s own settings` : 'Organization default, or one salon’s own settings'}
      />
      <SettingsBranchSelect value={salon} onChange={setSalon} />
      <div className="de-seg" role="tablist" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'whatsapp' ? <ProfileForm channel="whatsapp" fields={WHATSAPP_FIELDS} franchiseId={salon} /> : null}
      {tab === 'voice' ? <ProfileForm channel="voice" fields={VOICE_FIELDS} franchiseId={salon} /> : null}
      {tab === 'rules' ? <BranchRules /> : null}
    </div>
  );
}
